import type { DecisionLedgerPortfolioPackageDto } from './decision-ledger';

type PortfolioIntegrationSource = DecisionLedgerPortfolioPackageDto['integrationHealth']['sources'][number];

export type PortfolioFinanceTruthSummary = {
  headline: string;
  detail: string;
  tone: 'good' | 'warn' | 'risk';
};

function readText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function formatDate(value: string | null | undefined): string {
  const raw = readText(value);
  if (!raw) return 'Unknown';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed);
}

export function buildPortfolioFinanceTruthSummary(
  sources: PortfolioIntegrationSource[],
): PortfolioFinanceTruthSummary {
  const financeCost = sources.find((source) => readText(source?.source) === 'finance_cost') ?? null;
  const financeActuals =
    sources.find((source) => readText(source?.source) === 'finance_actuals') ?? null;

  const attributionGuardrail =
    'Helix will not present tenant-level accounting totals as portfolio-realized spend until scope attribution is deterministic.';

  if (financeActuals) {
    const financeActualsStatus = readText(financeActuals.status)?.toUpperCase() ?? 'UNKNOWN';
    const financeActualsGap = readText(financeActuals.dataGap);
    const modeledAttached = Boolean(financeCost);

    if (financeActualsStatus === 'OK') {
      return {
        tone: 'good',
        headline: modeledAttached
          ? 'Modeled portfolio cost and governed accounting evidence are both attached.'
          : 'Governed accounting evidence is attached.',
        detail: `Latest governed accounting evidence: ${formatDate(financeActuals.asOf)}. ${financeActualsGap ?? attributionGuardrail}`,
      };
    }

    return {
      tone: 'warn',
      headline: modeledAttached
        ? 'Finance remains modeled while governed accounting evidence is incomplete.'
        : 'Governed accounting evidence is incomplete.',
      detail: `${financeActualsGap ?? 'Governed accounting evidence is missing or stale.'} ${attributionGuardrail}`,
    };
  }

  if (financeCost) {
    return {
      tone: 'warn',
      headline: 'Finance remains modeled only.',
      detail:
        'Helix has modeled cost readiness for this plan, but no governed accounting evidence is attached. '
        + attributionGuardrail,
    };
  }

  return {
    tone: 'risk',
    headline: 'Finance truth posture is incomplete.',
    detail:
      'No modeled finance readiness or governed accounting evidence is attached to this plan.',
  };
}
